import { createBrowserRouter, createMemoryRouter, RouterProvider } from 'react-router-dom';

/**
 * Determines if the current environment is the browser
 * @returns {boolean}
 */
function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

import App from './App';
import { Layout } from './components/layout';

// Import company pages
import CompanyPage from './pages/company';
import AboutPage from './pages/company/about';
import CareersPage from './pages/company/careers';
import ContactPage from './pages/company/contact';
import PressPage from './pages/company/press';

// Import booking page
import BookingPage from './pages/BookingPage';

// Import privacy policy page
import PrivacyPolicyPage from './pages/privacy-policy';
// Import terms of service page
import TermsOfServicePage from './pages/terms-of-service';

// Import blog pages
import BlogListPage from './pages/blog';
// Import all static blog post pages (auto-generated below)
// For maintainability, consider auto-generating these imports

import BlogCategoryPage from './pages/blog/category/[slug]';
import BlogPostPage from './pages/blog/[slug]';

// Import industry pages
import IndustriesPage from './pages/industries';
import HealthcarePage from './pages/industries/healthcare';
import FinancePage from './pages/industries/finance';
import LegalPage from './pages/industries/legal';
import EducationPage from './pages/industries/education';
import ManufacturingPage from './pages/industries/manufacturing';
import RetailPage from './pages/industries/retail';
// Import static blog routes

// Import NotFound page
import NotFound from './pages/NotFound';

/**
 * Router configuration for the Raidu website
 * Defines all routes and their corresponding components
 */
const routes = [
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/company',
    element: <Layout><CompanyPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/company/about',
    element: <Layout><AboutPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/company/careers',
    element: <Layout><CareersPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/company/press',
    element: <Layout><PressPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/company/contact',
    element: <Layout><ContactPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/booking',
    element: <Layout><BookingPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/privacy-policy',
    element: <Layout><PrivacyPolicyPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/terms-of-service',
    element: <Layout><TermsOfServicePage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/blog',
    element: <Layout><BlogListPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/blog/category/:slug',
    element: <Layout><BlogCategoryPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/blog/:slug',
    element: <Layout><BlogPostPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  // Industry routes
  {
    path: '/industries',
    element: <Layout><IndustriesPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/industries/healthcare',
    element: <Layout><HealthcarePage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/industries/finance',
    element: <Layout><FinancePage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/industries/legal',
    element: <Layout><LegalPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/industries/education',
    element: <Layout><EducationPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/industries/manufacturing',
    element: <Layout><ManufacturingPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/industries/retail',
    element: <Layout><RetailPage /></Layout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

/**
 * Returns the correct router instance based on environment
 * Uses createBrowserRouter in browser, createMemoryRouter in SSG/SSR
 */
function getRouter() {
  try {
    if (isBrowser()) {
      console.log('[router.tsx] Using createBrowserRouter (SPA mode)');
      return createBrowserRouter(routes);
    } else {
      console.log('[router.tsx] Using createMemoryRouter (SSG/SSR mode)');
      return createMemoryRouter(routes, { initialEntries: ['/'] });
    }
  } catch (error) {
    console.error('[router.tsx] Failed to create router:', error);
    throw error;
  }
}

/**
 * Error boundary component for handling routing errors
 * @returns {JSX.Element} The rendered error component
 */
function ErrorBoundary() {
  try {
    console.error('Routing error occurred');
    
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="p-8 max-w-md bg-white rounded-lg shadow-xl">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Page Not Found</h1>
            <p className="text-gray-700 mb-4">The page you're looking for doesn't exist or you don't have access to it.</p>
            <a 
              href="/" 
              className="inline-block px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
              onClick={() => {
                try {
                  console.log('User clicked to return to home from error page');
                } catch (error) {
                  console.error('Error handling return to home click:', error);
                }
              }}
            >
              Return to Home
            </a>
          </div>
        </div>
      </Layout>
    );
  } catch (error) {
    console.error('Error rendering ErrorBoundary component:', error);
    return <div className="p-4 bg-red-100 text-red-800">Something went wrong</div>;
  }
}

/**
 * Router component that provides routing functionality to the app
 * @returns {JSX.Element} The router provider with configured routes
 */
export function AppRouter() {
  try {
    const router = getRouter();
    console.log('[AppRouter] Initializing router');
    return <RouterProvider router={router} />;
  } catch (error) {
    console.error('[AppRouter] Error initializing router:', error);
    return <div className="p-4 bg-red-100 text-red-800">Failed to initialize routing</div>;
  }
}


